import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Box, SxProps, Tab, Tabs, TabsOwnProps } from "@mui/material";
import clsx from "clsx";

interface ITabPanelProps {
	children?: ReactNode;
	identifier: string;
	value: number;
	index: number;
}

const TabPanel = ({ children, identifier, value, index }: Readonly<ITabPanelProps>) => {
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${identifier}-${index}`}
			aria-labelledby={`simple-tab-${identifier}-${index}`}
		>
			{value === index && children}
		</div>
	);
};

const generateTabProps = (identifier: string, index: number) => {
	return {
		id: `simple-tab-${identifier}-${index}`,
		"aria-controls": `simple-tabpanel-${identifier}-${index}`,
	};
};

interface IDashboardTabsProps {
	initialTab?: number;
	data: Array<{
		tabLabel: string;
		tabKey?: string;
		tabContent: ReactNode;
		disabled?: boolean;
	}>;
	tabsIdentifier: string;
	tabStyles?: SxProps;
	tabClass?: string;
	tabListClass?: string;
	extraHeaderContent?: ReactNode;
	onChange?: (tabLabel: string) => void;
	onTabClick?: (tabLabel: string) => void;
}

const DashboardTabs = (props: Readonly<IDashboardTabsProps>) => {
	const { data, tabsIdentifier, tabClass, tabListClass, extraHeaderContent: tabHeaderIcon, onChange, initialTab } = props;

	const [value, setValue] = useState(initialTab ?? 0);
	const handleTabChange: TabsOwnProps["onChange"] = (_e, newValue) => {
		setValue(newValue);
		onChange?.(data[newValue].tabLabel);
	};
	const handleTabClick = (tabLabel: string) => {
		props.onTabClick?.(tabLabel);
	};

	return (
		<Box>
			<Box className="relative border-b-[1px] border-disabled">
				<Tabs
					TabIndicatorProps={{
						className: "!h-[3px] flex content-center !bg-transparent",
						children: <span className="!w-3/5 !bg-primary mx-auto rounded-t-[10px]" />,
					}}
					className={twMerge("!min-h-[30px] [&_.MuiTabs-flexContainer]:gap-[1.5em]", tabListClass)}
					value={value}
					onChange={handleTabChange}
					aria-label="dashboard-tabs"
				>
					{data?.map((item, i) => {
						const mainKey = item?.tabKey ?? `${i}`;
						const keyIdentifier = `${tabsIdentifier}_${item.tabLabel}_${mainKey}`;
						return (
							<Tab
								key={keyIdentifier}
								label={item.tabLabel}
								disabled={item.disabled}
								disableRipple
								{...generateTabProps(tabsIdentifier, i)}
								className={twMerge(
									"[&.Mui-selected]:!text-primary !font-montserrat ![text-transform:unset] !text-neutral-gray !min-h-0 !min-w-0 !p-0 !pb-2",
									tabClass,
									clsx({ "!text-disabled": item.disabled })
								)}
								onClick={() => {
									handleTabClick(item.tabLabel);
								}}
							/>
						);
					})}
				</Tabs>
				{tabHeaderIcon && <Box className="absolute right-0 top-0">{tabHeaderIcon}</Box>}
			</Box>
			<Box className="pt-4">
				{data?.map((item, i) => {
					const mainKey = item?.tabKey ?? `${i}`;
					const keyIdentifier = `${tabsIdentifier}_${item.tabLabel}_${mainKey}`;
					return (
						<TabPanel key={keyIdentifier} identifier={tabsIdentifier} value={value} index={i}>
							{item.tabContent}
						</TabPanel>
					);
				})}
			</Box>
		</Box>
	);
};

export default DashboardTabs;
